/**
 * S5 \u5173\u5361\u4e0e\u76ee\u6807 \u2014\u2014 \u5b9e\u73b0\u81ea design/gdd/systems/S5-levels-goals.md\u3002
 * \u672c\u5207\u7247\u53ea\u505a\u7b2c 1\u20133 \u5173\uff08\u6559\u5b66\u6bb5\uff09\uff0c\u4e14\u4ec5\u542f\u7528\u63a8\u8fdb\u5668\u3002
 */

import type { PieceColor } from './types';

export type GoalType = 'ELIMINATE_COLOR';

export interface Goal {
  type: GoalType;
  color: PieceColor;
  count: number;
}

export interface ReferencePath {
  name: string;
  description: string;
  estimatedScore: number;
  dependsOnNewSpecial: boolean;
}

export interface LevelData {
  levelId: number;
  teachingGoal: string;
  moves: number;
  goals: Goal[];
  obstacles: number;
  maxRunPerLine: number;
  expectedScore: number;
  starLine2: number;
  starLine3: number;
  referencePaths: ReferencePath[];
}

// \u2500\u2500\u2500\u2500 S5 \u00a72.4\uff1a\u8def\u5f84\u4f30\u7b97\u516c\u5f0f\uff08C \u6863\uff09 \u2500\u2500\u2500\u2500

/** \u8def\u5f84 A\uff1a\u7a33\u5065\u7b56\u7565 */
export function pathScoreA(moves: number, sp: number): number {
  return moves * 220 + 0.5 * sp + 90;
}

/** \u8def\u5f84 B\uff1a\u6fc0\u8fdb\u7b56\u7565 */
export function pathScoreB(moves: number, sp: number): number {
  return moves * 164 + sp + 150;
}

/** \u7279\u6b8a\u5757\u4f30\u5206\uff08sp\uff09 */
export const SP_TABLE: Record<number, number> = {
  1: 0, 2: 0, 3: 350, 4: 500, 5: 750, 6: 850, 7: 800, 8: 1000,
};

export function deriveStarLines(moves: number, sp: number): { star2: number; star3: number; expected: number } {
  const a = pathScoreA(moves, sp);
  const b = pathScoreB(moves, sp);
  const star3 = Math.round(Math.min(a, b) * 0.95);
  // expectedScore = starLine3 / starK3\uff08starK3 = 1.5\uff09
  const expected = Math.round(star3 / 1.5);
  return { star2: Math.round(expected * 1.25), star3, expected };
}

/** \u672c\u5207\u7247\u5173\u5361\u8868\uff08\u53d6 S5 \u00a72.3 \u524d\u4e09\u5173\uff09 */
export const SLICE_LEVELS: LevelData[] = [
  {
    levelId: 1,
    teachingGoal: '\u8ba4\u8bc6\u57fa\u7840\u4e09\u6d88\uff1a\u62d6\u62fd\u4e0e\u70b9\u51fb\u4e24\u79cd\u64cd\u4f5c',
    moves: 15,
    goals: [{ type: 'ELIMINATE_COLOR', color: 'earth', count: 10 }],
    obstacles: 0,
    maxRunPerLine: 3,
    expectedScore: 1500,
    starLine2: 1900,
    starLine3: 2250,
    referencePaths: [
      { name: '\u7a33\u5065\u6d88\u9664', description: '\u4f18\u5148\u6253\u5730\u7403\uff0c\u4e0d\u8ffd\u6c42\u8fde\u9501', estimatedScore: 2340, dependsOnNewSpecial: false },
      { name: '\u540c\u8272\u805a\u7126', description: '\u5236\u9020\u591a\u7ec4\u5730\u7403\u76f8\u90bb\uff0c\u4e00\u6b65\u591a\u7ec4', estimatedScore: 2290, dependsOnNewSpecial: false },
    ],
  },
  {
    levelId: 2,
    teachingGoal: '\u8ba4\u8bc6\u706b\u661f\uff1a\u5206\u8fa8\u7b2c\u4e8c\u79cd\u989c\u8272\u76ee\u6807',
    moves: 15,
    goals: [{ type: 'ELIMINATE_COLOR', color: 'mars', count: 12 }],
    obstacles: 0,
    maxRunPerLine: 3,
    expectedScore: 1500,
    starLine2: 1900,
    starLine3: 2250,
    referencePaths: [
      { name: '\u7a33\u5065\u6d88\u9664', description: '\u4f18\u5148\u6253\u706b\u661f\uff0c\u6bcf\u6b65\u81f3\u5c11\u4e00\u7ec4', estimatedScore: 2340, dependsOnNewSpecial: false },
      { name: '\u8fde\u9501\u8d77\u6b65', description: '\u5236\u9020\u4e8c\u7ea7\u8fde\u9501', estimatedScore: 2300, dependsOnNewSpecial: false },
    ],
  },
  {
    levelId: 3,
    teachingGoal: '\u8ba4\u8bc6\u91d1\u661f\u4e0e\u63a8\u8fdb\u5668\uff1a\u56db\u8fde\u751f\u6210\u7279\u6b8a\u5757',
    moves: 14,
    goals: [{ type: 'ELIMINATE_COLOR', color: 'venus', count: 13 }],
    obstacles: 0,
    maxRunPerLine: 3,
    expectedScore: 1800,
    starLine2: 2250,
    starLine3: 2700,
    referencePaths: [
      { name: '\u7a33\u5065\u6d88\u9664', description: '\u4f18\u5148\u6253\u91d1\u661f', estimatedScore: 3145, dependsOnNewSpecial: false },
      { name: '\u63a8\u8fdb\u5668\u5f15\u7206', description: '\u56db\u8fde\u751f\u6210\u63a8\u8fdb\u5668\u540e\u4e3b\u52a8\u4ea4\u6362\u5f15\u7206', estimatedScore: 3290, dependsOnNewSpecial: true },
    ],
  },
];

export function getLevel(levelId: number): LevelData | undefined {
  return SLICE_LEVELS.find((l) => l.levelId === levelId);
}

/** 当前构建包含的关卡总数（用于"下一关"动态上限，替代硬编码 3） */
export function getLevelCount(): number {
  return SLICE_LEVELS.length;
}

/** G-5a\uff1a\u52a0\u8f7d\u65f6\u65ad\u8a00\u6bcf\u6761\u8def\u5f84 estimatedScore \u2265 starLine3 */
export function validateLevel(level: LevelData): { ok: boolean; reason?: string } {
  if (level.referencePaths.length < 2) {
    return { ok: false, reason: '\u53c2\u8003\u8def\u5f84\u5c11\u4e8e 2 \u6761\uff08G-5a\uff09' };
  }
  for (const p of level.referencePaths) {
    if (p.estimatedScore < level.starLine3) {
      return { ok: false, reason: `\u8def\u5f84\u300c${p.name}\u300d\u4f30\u5206 ${p.estimatedScore} < starLine3 ${level.starLine3}\uff08G-5a\uff09` };
    }
  }
  if (level.goals.length === 0) return { ok: false, reason: '\u65e0\u76ee\u6807' };
  return { ok: true };
}
